import type { SelectOption } from '@movingimage-evp/mi-ui-component-library';

import type { RtmpParametersInput, UpdateWebcastInput } from '../../../../generated/graphql-manager';

import {
  Heading,
  InputDescription,
  Paragraph,
  Radio,
  Select,
  SelectionInputLayout,
  Toggle,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { InfoTooltip } from 'src/components/info-tooltip';
import { useCurrentUser } from 'src/manager/hooks/current-user';
import { useStreamingProfiles } from 'src/manager/hooks/use-streaming-profiles';

import {
  IngestProtocol,
  State,
  useGetLsproDefaultSettingsQuery,
  useGetWebcastProtocolQuery,
  useUpdateWebcastProtocolMutation,
} from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { SetupPageFooter } from '../../../components/setup-page-footer';
import { useUserPermissions } from '../../../hooks/user-permissions';

import styles from './protocol-settings.module.css';
import managerStyles from '../../../manager.module.css';

export function ProtocolSettingsPage() {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();
  const { lsproId } = useCurrentUser();
  const routes = useAbsoluteRoutes();
  const { isEventEditingAllowed } = useUserPermissions();

  const { data, loading } = useGetWebcastProtocolQuery({
    variables: { webcastId },
    skip: !webcastId,
  });

  const { data: settingsData } = useGetLsproDefaultSettingsQuery({ variables: { lsproId } });

  const defaultSettings = settingsData?.lsproSettings?.defaultSettings;
  const transcodingProfileId = data?.webcast?.streaming?.live?.transcodingProfileId;

  const streamingProfiles = useStreamingProfiles();
  const chosenStreamingProfile =
    streamingProfiles.find(({ key }) => key === transcodingProfileId) || streamingProfiles[0];

  const handleStreamingProfileChange = (option: SelectOption) => {
    if (!data?.webcast?.streaming?.live) return;

    updateWebcastProtocol({
      variables: {
        input: {
          id: webcastId,
          transcodingProfileId: option.key,
        },
      },
      optimisticResponse: {
        updateWebcast: {
          __typename: 'UpdateWebcastSuccess',
          webcast: {
            id: webcastId,
            streaming: {
              live: {
                ...data?.webcast?.streaming?.live,
                transcodingProfileId: option.key,
              },
            },
          },
        },
      },
      onCompleted: (data) => {
        if (data?.updateWebcast.__typename === 'UpdateWebcastSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const ecdnAvailable = !loading && !!data?.webcast?.ecdnSettings;
  const ingestProtocol = data?.webcast?.streaming?.live?.ingestProtocol;
  const streamingParameters = data?.webcast?.streaming?.live?.parameters;
  const rtmpAuthenticationEnabled =
    streamingParameters?.__typename === 'RtmpLiveStreamingParameters' && streamingParameters?.authenticationEnabled;

  const [updateWebcastProtocol] = useUpdateWebcastProtocolMutation();

  function handleProtocolChange(ingestProtocol: IngestProtocol, authenticationEnabled?: boolean) {
    const input: UpdateWebcastInput = { id: webcastId };

    if (ingestProtocol === IngestProtocol.RTMP) {
      const parameters: RtmpParametersInput = {};

      if (authenticationEnabled !== undefined) {
        parameters.authenticationEnabled = authenticationEnabled;
      }

      input.ingest = { rtmp: { parameters } };
    }

    if (ingestProtocol === IngestProtocol.SRT) {
      input.ingest = { srt: { parameters: {} } };
    }

    updateWebcastProtocol({
      variables: { input },
      optimisticResponse: {
        updateWebcast: {
          __typename: 'UpdateWebcastSuccess',
          webcast: {
            __typename: 'Webcast',
            id: webcastId,
            streaming: data?.webcast?.streaming,
          },
        },
      },
      update: (cache, { data }) => {
        if (data?.updateWebcast.__typename !== 'UpdateWebcastSuccess') return;

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: {
            streaming(existing) {
              let parameters;

              if (ingestProtocol === IngestProtocol.RTMP) {
                parameters = {
                  __typename: 'RtmpLiveStreamingParameters',
                  authenticationEnabled: authenticationEnabled ?? false,
                };
              } else {
                parameters = { __typename: 'SrtLiveStreamingParameters' };
              }

              return {
                ...existing,
                live: {
                  ...existing.live,
                  ingestProtocol,
                  parameters,
                },
              };
            },
          },
        });
      },
      onCompleted: (data) => {
        if (data?.updateWebcast.__typename === 'UpdateWebcastSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  }

  const formDisabled = data?.webcast.state !== State.PRELIVE || !isEventEditingAllowed;

  return (
    <main className={managerStyles.main} data-testid="protocol-settings-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.protocolSettings.title')}</Heading>
      <Paragraph className={managerStyles.grayText}>{t('manager.webcastSetup.protocolSettings.description')}</Paragraph>

      <div className={styles.options}>
        <SelectionInputLayout
          checked={ingestProtocol === IngestProtocol.RTMP}
          disabled={formDisabled}
          inputElement={
            <div className={styles.option}>
              <Radio
                data-testid="rtmp-radio"
                name="protocol"
                value={IngestProtocol.RTMP}
                checked={ingestProtocol === IngestProtocol.RTMP}
                disabled={formDisabled}
                onChange={() => handleProtocolChange(IngestProtocol.RTMP)}
              >
                {t('manager.webcastSetup.protocolSettings.options.rtmps.label')}
              </Radio>

              <InfoTooltip
                label={t('manager.webcastSetup.protocolSettings.tooltip.rtmps.title')}
                className={styles.infoIcon}
              />
            </div>
          }
        />

        {ingestProtocol === IngestProtocol.RTMP && (
          <SelectionInputLayout
            disabled={formDisabled}
            className={styles.inputLayout}
            hintMessage={<span>{t('manager.webcastSetup.protocolSettings.options.rtmps.authentication.hint')}</span>}
            inputElement={
              <Toggle
                data-testid="rtmp-authentication-toggle"
                checked={rtmpAuthenticationEnabled}
                disabled={formDisabled}
                onChange={() => handleProtocolChange(IngestProtocol.RTMP, !rtmpAuthenticationEnabled)}
              >
                {t('manager.webcastSetup.protocolSettings.options.rtmps.authentication.label')}
              </Toggle>
            }
          />
        )}

        <SelectionInputLayout
          checked={ingestProtocol === IngestProtocol.SRT}
          disabled={formDisabled}
          inputElement={
            <div className={styles.option}>
              <Radio
                data-testid="srt-radio"
                name="protocol"
                value={IngestProtocol.SRT}
                checked={ingestProtocol === IngestProtocol.SRT}
                disabled={formDisabled}
                onChange={() => handleProtocolChange(IngestProtocol.SRT)}
              >
                {t('manager.webcastSetup.protocolSettings.options.srt.label')}
              </Radio>

              <InfoTooltip
                label={t('manager.webcastSetup.protocolSettings.tooltip.srt.title')}
                className={styles.infoIcon}
              />
            </div>
          }
        />

        {defaultSettings?.allowTranscodingProfileSelection && chosenStreamingProfile && (
          <InputDescription label={t('manager.webcastSetup.protocolSettings.streamingProfileSelect.label')}>
            <Select
              data-testid="streaming-profile-select"
              optionsIdProperty="key"
              value={chosenStreamingProfile}
              options={streamingProfiles}
              disabled={formDisabled}
              onChange={handleStreamingProfileChange}
            />
          </InputDescription>
        )}
      </div>

      <SetupPageFooter
        nextLabel={
          ecdnAvailable
            ? t('manager.webcastSetup.protocolSettings.nextStep.labelEcdn')
            : t('manager.webcastSetup.protocolSettings.nextStep.labelSummary')
        }
        route={ecdnAvailable ? routes.webcastSetup_ecdn : routes.webcastSetup_setupSummary}
        nextButtonDisabled={loading}
      />
    </main>
  );
}
